/**
 * Support Cases -- Aaim-operator queue (/support).
 *
 * platform.aaim.com:9303 (Aaim-operator SPA). This is the operator-run console:
 * Aaim staff triage and work EVERY support case across all tenants while each
 * ticket is homed to the consumer's FI tenant (operator-run, tenant-homed --
 * see sprint-queue/sprint-aaim-e-build/node-5-operator-console-PLAN.md).
 *
 * Audience: platform-admin ONLY (AAIM_SUPER_ADMIN / platform_admin /
 * E2E_TEST_ADMIN). The FI-operator self-service view of their own tenant's
 * cases lives in admin-portal (admin.aaim.com), flag-gated, built later.
 * Anyone else lands on the 403 panel.
 *
 * Data: GET /api/v1/support/cases (all tenants) via supportPlatformClient.
 * Types come straight from the generated support-platform spec; no view-model
 * interfaces (anonymous useState shapes, named `components['schemas']` aliases).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BodyText,
  Badge,
  Card,
  DataTable,
  type DataTableColumn,
  Select,
  Alert,
} from '@aaimplatform/ui';
import type { components } from '@aaimplatform/institutional-types/support-platform';
import { useAuthStore } from '../../stores/auth';
import { supportPlatformClient } from '../../api/typed-clients';
import {
  SUPPORT_SERVICE_MISSING_TITLE,
  SupportServiceMissingError,
  SupportServiceNotice,
  supportFailure,
} from './SupportServiceNotice';

type SupportCase = components['schemas']['SupportCase'];
type SupportCaseStatus = components['schemas']['SupportCaseStatus'];
type SupportCasePriority = components['schemas']['SupportCasePriority'];

// Operator audience (lowercase per authz-case-insensitive).
const PLATFORM_ADMIN_ROLES = ['aaim_super_admin', 'platform_admin', 'e2e_test_admin'] as const;

function statusVariant(status: SupportCaseStatus): 'success' | 'warning' | 'info' | 'neutral' | 'error' {
  switch (status) {
    case 'open':
    case 'awaiting_platform_admin':
      return 'warning';
    case 'awaiting_user':
    case 'awaiting_fi_input':
      return 'info';
    case 'resolved':
      return 'success';
    case 'closed':
      return 'neutral';
    default:
      return 'neutral';
  }
}

function priorityVariant(priority: SupportCasePriority): 'success' | 'warning' | 'info' | 'neutral' | 'error' {
  switch (priority) {
    case 'P1':
      return 'error';
    case 'P2':
      return 'warning';
    case 'P3':
      return 'info';
    case 'P4':
      return 'neutral';
    default:
      return 'neutral';
  }
}

export function SupportCasesPage() {
  const user = useAuthStore((s) => s.user);
  const roles = useMemo(
    () => (user?.roles ?? []).map((r) => (typeof r === 'string' ? r.toLowerCase() : r)),
    [user],
  );
  const isPlatformAdmin = PLATFORM_ADMIN_ROLES.some((r) => roles.includes(r));

  const [cases, setCases] = useState<SupportCase[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [serviceMissing, setServiceMissing] = useState<boolean>(false);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [priorityFilter, setPriorityFilter] = useState<string>('');
  const [assignedToMe, setAssignedToMe] = useState<boolean>(false);

  const loadCases = useCallback(async () => {
    if (!isPlatformAdmin) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    setServiceMissing(false);
    try {
      const params: Record<string, string | number | boolean> = {};
      if (statusFilter) params.status = statusFilter;
      if (priorityFilter) params.priority = priorityFilter;
      if (assignedToMe) params.assignedToMe = true;

      const { data, error, response } = await supportPlatformClient.GET('/api/v1/support/cases', {
        params: { query: params as never },
      });
      if (error !== undefined || !response.ok) throw supportFailure(response, error, 'Failed to load cases');
      setCases(data?.cases ?? []);
    } catch (err) {
      if (err instanceof SupportServiceMissingError) setServiceMissing(true);
      else setLoadError(err instanceof Error ? err.message : 'Failed to load cases');
      setCases([]);
    } finally {
      setLoading(false);
    }
  }, [isPlatformAdmin, statusFilter, priorityFilter, assignedToMe]);

  useEffect(() => {
    void loadCases();
  }, [loadCases]);

  if (!isPlatformAdmin) {
    return (
      <div className="p-6 space-y-4">
        <Card>
          <div className="p-4">
            <Alert type="error">
              You do not have access to the operator support queue. This surface is reserved
              for Aaim platform-admin agents.
            </Alert>
          </div>
        </Card>
      </div>
    );
  }

  const columns: DataTableColumn<SupportCase>[] = [
    {
      header: 'Reference',
      accessor: 'reference',
      cell: (_value, row) => (
        <Link to={`/support/${row.id}`} className="text-primary hover:underline">
          {row.reference}
        </Link>
      ),
    },
    {
      header: 'Scope',
      accessor: 'tenantId',
      // Homed tenant: consumer cases carry consumerUserId (homed to their FI
      // tenant once Node A re-homing lands); FI-operator cases carry no
      // consumerUserId. A tenant-name resolver is a follow-on; show the source
      // + the homed tenant id (truncated) honestly for now.
      cell: (_value, row) => (
        <span className="text-secondary text-sm">
          {row.consumerUserId ? 'Consumer' : 'FI'} &middot; {String(row.tenantId).slice(0, 8)}
        </span>
      ),
    },
    { header: 'Category', accessor: 'category' },
    {
      header: 'Status',
      accessor: 'status',
      cell: (_value, row) => (
        <Badge variant={statusVariant(row.status)} size="sm">
          {row.status}
        </Badge>
      ),
    },
    {
      header: 'Priority',
      accessor: 'priority',
      cell: (_value, row) => (
        <Badge variant={priorityVariant(row.priority)} size="sm">
          {row.priority}
        </Badge>
      ),
    },
    {
      header: 'Opened',
      accessor: 'openedAt',
      cell: (_value, row) => (
        <span className="text-secondary text-sm">
          {row.openedAt ? new Date(row.openedAt).toLocaleString() : '-'}
        </span>
      ),
    },
    { header: 'Channel', accessor: 'sourceChannel' },
  ];

  const statusOptions = [
    { value: '', label: 'All statuses' },
    { value: 'open', label: 'Open' },
    { value: 'awaiting_user', label: 'Awaiting user' },
    { value: 'awaiting_platform_admin', label: 'Awaiting platform admin' },
    { value: 'awaiting_fi_input', label: 'Awaiting FI input' },
    { value: 'resolved', label: 'Resolved' },
    { value: 'closed', label: 'Closed' },
  ];

  const priorityOptions = [
    { value: '', label: 'All priorities' },
    { value: 'P1', label: 'P1 (critical)' },
    { value: 'P2', label: 'P2 (high)' },
    { value: 'P3', label: 'P3 (normal)' },
    { value: 'P4', label: 'P4 (low)' },
  ];

  return (
    <div className="p-6 space-y-4">
      <BodyText size="sm" color="secondary">
        Operator case queue across all consumer and FI-operator cases. Aaim works every case;
        each ticket is homed to its tenant.
      </BodyText>

      {serviceMissing && <SupportServiceNotice />}
      {loadError && <Alert type="error">{loadError}</Alert>}

      <Card>
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Select
              label="Status"
              options={statusOptions}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            />
            <Select
              label="Priority"
              options={priorityOptions}
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
            />
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm text-primary">
                <input
                  type="checkbox"
                  checked={assignedToMe}
                  onChange={(e) => setAssignedToMe(e.target.checked)}
                />
                Assigned to me
              </label>
            </div>
          </div>

          <DataTable<SupportCase>
            columns={columns}
            data={cases}
            rowKey="id"
            striped
            hoverable
            emptyMessage={
              loading
                ? 'Loading...'
                : serviceMissing
                  ? `${SUPPORT_SERVICE_MISSING_TITLE}.`
                  : 'No cases match the current filters.'
            }
          />
        </div>
      </Card>
    </div>
  );
}
